import React from "react";
import { useTranslation } from "react-i18next";
import { ScenariaPlan } from "src/model/model";

interface PlanPreviewProps {
  plan: ScenariaPlan;
}

const LENGTH_KEYS: Record<string, string> = {
  short: "pages.chat.planPreview.lengthShort",
  medium: "pages.chat.planPreview.lengthMedium",
  long: "pages.chat.planPreview.lengthLong",
};

// Display overrides for interaction chips when the LLM-emitted title disagrees
// with the canonical app-wide name (see NameFormatter.ts). Single-entry today:
// the content_avatar_ai_persona row is seeded with title="AvatarAIPersona"
// (PascalCase) while the rest of the app displays it as "People AI".
const INTERACTION_DISPLAY_OVERRIDES: Readonly<Record<string, string>> = Object.freeze({
  AvatarAIPersona: "People AI",
});

// Failsafe lookup: returns the override only when raw is a non-empty string,
// the key is the row's own property (no prototype chain), and the override
// itself is a non-empty string. Falls back to raw on any anomaly.
const formatInteractionLabel = (raw: unknown): string => {
  if (typeof raw !== "string" || raw.length === 0) return "";
  if (!Object.prototype.hasOwnProperty.call(INTERACTION_DISPLAY_OVERRIDES, raw)) {
    return raw;
  }
  const override = INTERACTION_DISPLAY_OVERRIDES[raw];
  return typeof override === "string" && override.length > 0 ? override : raw;
};

const PlanPreview: React.FC<PlanPreviewProps> = ({ plan }) => {
  const { t } = useTranslation();
  const pp = "pages.chat.planPreview";

  const mediaBadgeLabel = (mediaType: string) =>
    mediaType === "video360" ? t(`${pp}.mediaVideo360`) : t(`${pp}.mediaImage360`);

  const mediaBadgeClass = (mediaType: string) =>
    mediaType === "video360"
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : "bg-purple-50 text-purple-700 border-purple-200";

  const localizedLength = (raw: string) => {
    const key = LENGTH_KEYS[raw];
    return key ? t(key) : raw;
  };

  // Flat layout: PlanPreview only ever renders inside Chatbot's unified
  // plan block, so each meta section is now a typographic row separated
  // by hair-line dividers instead of an own card. Scene rows keep a
  // light bg-gray-50 pill so the list still reads as a list.
  return (
    <div className="px-3 py-3">
      <div className="divide-y divide-gray-100">
        <div className="pb-2.5">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {t(`${pp}.targetAudience`)}
          </h4>
          <p className="text-sm text-gray-700 mt-0.5">{plan.targetAudience}</p>
        </div>

        <div className="py-2.5">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {t(`${pp}.learningObjectives`)}
          </h4>
          <ul className="mt-0.5 list-disc list-inside text-sm text-gray-700 space-y-0.5">
            {plan.learningObjectives.map((o, i) => (
              <li key={i}>{o}</li>
            ))}
          </ul>
        </div>

        {plan.constraints && plan.constraints.length > 0 && (
          <div className="py-2.5">
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {t(`${pp}.constraints`)}
            </h4>
            <ul className="mt-0.5 list-disc list-inside text-sm text-gray-700 space-y-0.5">
              {plan.constraints.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        )}

        {plan.stylisticPreferences &&
          (plan.stylisticPreferences.tone || plan.stylisticPreferences.length) && (
            <div className="py-2.5">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {t(`${pp}.stylisticPreferences`)}
              </h4>
              <div className="text-sm text-gray-700 mt-0.5 space-y-0.5">
                {plan.stylisticPreferences.tone && (
                  <div>
                    <span className="font-medium">{t(`${pp}.tone`)} :</span>{" "}
                    {plan.stylisticPreferences.tone}
                  </div>
                )}
                {plan.stylisticPreferences.length && (
                  <div>
                    <span className="font-medium">{t(`${pp}.length`)} :</span>{" "}
                    {localizedLength(plan.stylisticPreferences.length)}
                  </div>
                )}
              </div>
            </div>
          )}

        <div className="pt-2.5">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
            {t(`${pp}.sceneOutline`)}
          </h4>
          <div className="space-y-1.5">
            {plan.sceneOutline.map((scene, i) => (
              <div key={scene.sceneId || i} className="rounded-md bg-gray-50 p-2.5">
                <div className="flex items-start justify-between gap-2">
                  <h5 className="text-sm font-semibold text-gray-800">
                    {t(`${pp}.sceneNumber`, { n: i + 1 })} — {scene.title}
                  </h5>
                  <span
                    className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full border ${mediaBadgeClass(
                      scene.mediaType,
                    )}`}
                  >
                    {mediaBadgeLabel(scene.mediaType)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{scene.summary}</p>
                {scene.mediaType === "video360" && (
                  <p className="mt-2 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                    {t(`${pp}.video360Disclaimer`)}
                  </p>
                )}
                {scene.interactions && scene.interactions.length > 0 && (
                  <div className="mt-2">
                    <span className="text-[11px] font-medium text-gray-500">
                      {t(`${pp}.interactions`)} :
                    </span>
                    {/* CSS grid so every badge column within a scene shares
                        the width of the widest badge — keeps the intent
                        sentences left-aligned to a single x. items-center
                        vertically centers the badge with the first line of
                        intent text. */}
                    <ul
                      className="mt-1 grid gap-x-2 gap-y-1"
                      style={{ gridTemplateColumns: "auto 1fr" }}
                    >
                      {scene.interactions.map((inter, j) => (
                        <li key={`inter-${j}`} className="contents text-sm text-gray-700">
                          <span className="self-center justify-self-start text-center text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                            {formatInteractionLabel(inter.type)}
                          </span>
                          <span className="self-center leading-snug">{inter.intent}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanPreview;
